import { createApp } from "vue";
import App from "./App.vue";
import store from "./store";
import router from "./router";
import Axios from "./Axios";
import authHeader from "./services/auth-header";

import "./assets/css/nucleo-icons.css";
import "./assets/css/nucleo-svg.css";

import MaterialDashboard from "./material-dashboard";

import PrimeVue from 'primevue/config';
import Aura from '@primevue/themes/aura';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import Multiselect from 'vue-multiselect';

import 'primeicons/primeicons.css';

// PrimeVue Components
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Toolbar from 'primevue/toolbar';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Rating from 'primevue/rating';
import Tag from 'primevue/tag';
import FileUpload from 'primevue/fileupload';
import DatePicker from 'primevue/datepicker';

import { definePreset } from '@primevue/themes';

const MyPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{yellow.50}',
            100: '{yellow.100}',
            200: '{yellow.200}',
            300: '{yellow.300}',
            400: '{yellow.400}',
            500: '{yellow.500}',
            600: '{yellow.600}',
            700: '{yellow.700}',
            800: '{yellow.800}',
            900: '{yellow.900}',
            950: '{yellow.950}'
        }
    }
});

// Load user from localStorage if exists
const storedUser = localStorage.getItem("user_free");
if (storedUser) {
    const userData = JSON.parse(storedUser);
    store.commit("auth/SET_USER", userData);
}

// Auth Guard
// Add a request interceptor to Axios
Axios.interceptors.request.use(
  (config) => {
    const headers = authHeader();
    if (headers.Authorization) {
      config.headers.Authorization = headers.Authorization;
    }
    if (headers.Accept) {
      config.headers.Accept = headers.Accept;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

router.beforeEach((to, from, next) => {
    const requiresAuth = to.meta?.requiresAuth ?? false;
    const requiredPermissions = to.meta?.permissions ?? [];
    const isLoggedIn = store.getters["auth/isLoggedIn"];

    // Special case for Google Auth redirect
    if (to.name === "GoogleAuthRedirect") {
        return next();
    }

    if (requiresAuth && !isLoggedIn) {
        return next({ name: "Login" });
    }

    // Check if user has required permissions for this route
    if (requiredPermissions.length > 0 && isLoggedIn) {
        const hasPermission = store.getters["auth/hasAnyPermission"];
        const isSuperAdmin = store.getters["auth/hasRole"]('super-admin');
        const currentUser = store.getters["auth/currentUser"];
        const isAdminType = currentUser?.type === 'admin';
        
        if (!isSuperAdmin && !isAdminType && !hasPermission(requiredPermissions)) {
            // User doesn't have required permissions, redirect to dashboard
            console.warn(`Access denied to ${to.path}. Required permissions:`, requiredPermissions);
            return next({ name: "Dashboard" });
        }
    }

    // Optional: prevent logged-in users from seeing login page
    if (to.name === "Login" && isLoggedIn && from.name) {
        next({ name: "Dashboard" });
    }

    return next();
});

// Create App
const appInstance = createApp(App);

// Register Global Components
appInstance.component('DataTable', DataTable);
appInstance.component('Column', Column);
appInstance.component('Toolbar', Toolbar);
appInstance.component('Button', Button);
appInstance.component('InputText', InputText);
appInstance.component('IconField', IconField);
appInstance.component('InputIcon', InputIcon);
appInstance.component('Rating', Rating);
appInstance.component('Tag', Tag);
appInstance.component('FileUpload', FileUpload);
appInstance.component('DatePicker', DatePicker);
appInstance.component('Multiselect', Multiselect);

// Register Plugins
appInstance.use(store);
appInstance.use(router);
appInstance.use(ToastService);
appInstance.use(PrimeVue, {
    theme: {
        preset: MyPreset,
    }
});
appInstance.use(MaterialDashboard);

// Register PrimeVue Directives
appInstance.directive('tooltip', Tooltip);

// Mount after router is ready
router.isReady().then(() => {
    appInstance.mount("#app");
});
